import { useState } from 'react';
import { Heart, CheckCircle, ArrowRight } from 'lucide-react';
import { useApplication } from '../../../hooks/queries/useApplications';
import { useSubmitCandidatePreferences } from '../../../hooks/mutations/useApplicationMutations';

const MATCHING_OPTIONS = {
  roles: ["Barista", "Server", "Cook", "Retail", "Cashier", "Warehouse"],
  availability: ["Full-Time", "Part-Time", "Weekends Only"],
  locations: ["Within 5 miles", "Within 10 miles", "Anywhere in City"],
};

interface CandidateRejectViewProps {
  applicationId?: string;
  candidateName?: string;
  company?: string;
}

type FlowState = 'message' | 'preferences' | 'dismissed' | 'success';

export default function CandidateRejectView({
  applicationId,
  candidateName: propCandidateName,
  company: propCompany,
}: CandidateRejectViewProps) {
  const { data: application } = useApplication(applicationId ?? "");
  const candidateName = application?.name ?? propCandidateName ?? "there";
  const company = application?.jobRole ?? propCompany ?? "the company";
  const [flowState, setFlowState] = useState<FlowState>('message');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const submitPreferences = useSubmitCandidatePreferences();

  const handleYesShare = () => {
    setFlowState('preferences');
  };

  const handleNoShare = () => {
    setFlowState('dismissed');
  };

  const toggleRole = (role: string) => {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const toggleAvailability = (availability: string) => {
    setSelectedAvailability(prev =>
      prev.includes(availability) ? prev.filter(a => a !== availability) : [...prev, availability]
    );
  };

  const toggleLocation = (location: string) => {
    setSelectedLocations(prev =>
      prev.includes(location) ? prev.filter(l => l !== location) : [...prev, location]
    );
  };

  const handleSubmitPreferences = () => {
    if (!applicationId) return;
    setSubmitError(null);
    submitPreferences.mutate(
      {
        applicationId,
        preferences: {
          roles: selectedRoles,
          availability: selectedAvailability,
          locations: selectedLocations,
        },
      },
      {
        onSuccess: () => setFlowState('success'),
        onError: () => setSubmitError("Something went wrong. Please try again."),
      },
    );
  };

  const canSubmit =
    selectedRoles.length > 0 &&
    selectedAvailability.length > 0 &&
    selectedLocations.length > 0;

  // Dismissed: clean navigation away
  if (flowState === 'dismissed') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <p className="text-xl font-bold text-gray-900 mb-2">Got it — thanks for your time.</p>
          <p className="text-base text-gray-500 mb-6">
            We wish you the best in your search.
          </p>
          <a
            href="#/jobs"
            className="inline-flex items-center gap-2 text-[#0156FF] font-semibold text-sm hover:underline"
          >
            Browse other open jobs <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  // Success view
  if (flowState === 'success') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" strokeWidth={2} />
          <h1 className="text-2xl font-extrabold text-gray-900 mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
            We're On It!
          </h1>
          <p className="text-base text-gray-600 mb-8">
            We'll search for openings that match what you're looking for and reach out when we find a fit.
          </p>

          {/* Browse jobs CTA */}
          <a
            href="#/jobs"
            className="inline-flex items-center justify-center gap-2 w-full h-12 rounded-full bg-[#0156FF] text-white text-base font-bold hover:bg-blue-700 transition-colors mb-4"
          >
            Browse Open Jobs <ArrowRight className="w-4 h-4" />
          </a>

          {/* App Up-Sell Card */}
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 mb-4">
            <p className="text-sm text-gray-700 mb-4">
              Get notified the moment we find a match.
            </p>
            <button
              type="button"
              className="w-full h-12 rounded-full border-2 border-gray-900 text-gray-900 text-base font-bold hover:bg-gray-900 hover:text-white transition-colors"
            >
              Download App
            </button>
          </div>

          <p className="text-sm text-gray-400">
            You can close this page now.
          </p>
        </div>
      </div>
    );
  }

  // State 1 & 2: Message + Preferences
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Header */}
        <header className="mb-6">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Heart className="w-7 h-7 text-gray-500" strokeWidth={2} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Thank you for applying, {candidateName}.
          </h1>
          <p className="text-base text-gray-700 leading-relaxed">
            After careful consideration, we've decided to move forward with other candidates
            for the <span className="font-semibold">{company}</span> role. This was a tough call —
            your application stood out.
          </p>
        </header>

        {/* Forward-looking message */}
        <p className="text-sm text-gray-600 leading-relaxed mb-6">
          The right opportunity is out there, and we want to help you find it.
        </p>

        {/* The "Pivot" Card */}
        <section className="bg-blue-50 border border-blue-100 rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Don't stop here.</h2>
          <p className="text-sm text-gray-700 mb-5 leading-relaxed">
            We work with 50+ employers hiring for similar roles right now. Can we share your profile with them to find you a match?
          </p>

          {flowState === 'message' && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={handleYesShare}
                className="w-full h-12 rounded-full bg-[#0156FF] text-white text-base font-bold hover:bg-blue-700 transition-colors"
              >
                Yes, find me something
              </button>
              <button
                type="button"
                onClick={handleNoShare}
                className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors py-2"
              >
                No thanks
              </button>
            </div>
          )}

          {flowState === 'preferences' && (
            <p className="text-sm text-gray-600 italic">Tell us what you're looking for below.</p>
          )}
        </section>

        {/* Browse other jobs */}
        {flowState === 'message' && (
          <div className="text-center mb-6">
            <a
              href="#/jobs"
              className="inline-flex items-center gap-1.5 text-[#0156FF] font-semibold text-sm hover:underline"
            >
              Browse other open jobs <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        )}

        {/* The Preference Form (State 2) */}
        {flowState === 'preferences' && (
          <section className="space-y-6 mb-8">
            {/* Interested In */}
            <div>
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-1">
                Interested In
              </h3>
              <p className="text-xs text-gray-500 mb-3">Select all that apply.</p>
              <div className="flex flex-wrap gap-2">
                {MATCHING_OPTIONS.roles.map((role) => {
                  const isSelected = selectedRoles.includes(role);
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => toggleRole(role)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                        isSelected
                          ? 'bg-[#0156FF] text-white border-none'
                          : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {role}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Availability */}
            <div>
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-1">
                Availability
              </h3>
              <p className="text-xs text-gray-500 mb-3">Select all that apply.</p>
              <div className="flex flex-wrap gap-2">
                {MATCHING_OPTIONS.availability.map((availability) => {
                  const isSelected = selectedAvailability.includes(availability);
                  return (
                    <button
                      key={availability}
                      type="button"
                      onClick={() => toggleAvailability(availability)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                        isSelected
                          ? 'bg-[#0156FF] text-white border-none'
                          : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {availability}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Distance */}
            <div>
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-1">
                Distance
              </h3>
              <p className="text-xs text-gray-500 mb-3">How far are you willing to travel?</p>
              <div className="flex flex-wrap gap-2">
                {MATCHING_OPTIONS.locations.map((location) => {
                  const isSelected = selectedLocations.includes(location);
                  return (
                    <button
                      key={location}
                      type="button"
                      onClick={() => toggleLocation(location)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                        isSelected
                          ? 'bg-[#0156FF] text-white border-none'
                          : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {location}
                    </button>
                  );
                })}
              </div>
            </div>

            {submitError && (
              <p className="text-sm text-red-600">{submitError}</p>
            )}

            {/* Submit Button */}
            <button
              type="button"
              onClick={handleSubmitPreferences}
              disabled={!canSubmit || submitPreferences.isPending}
              className={`w-full h-14 rounded-full text-lg font-bold transition-all ${
                canSubmit && !submitPreferences.isPending
                  ? 'bg-[#0156FF] text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {submitPreferences.isPending ? "Submitting..." : "Find My Match"}
            </button>
          </section>
        )}
      </div>
    </div>
  );
}
